/**
 * GeoJSON to OpenLayers Vector Layer Converter
 *
 * This utility converts GeoJSON FeatureCollections to OpenLayers VectorLayer instances
 * with proper styling, visibility, and z-order configuration.
 *
 * Features:
 * - Supports Point, LineString, and Polygon geometry types
 * - Applies symbology (fill color, stroke color, stroke width, opacity, marker properties)
 * - Handles layer visibility and z-order
 * - Validates GeoJSON structure
 * - Supports coordinate reference system (CRS) transformations
 * - Geometry-specific styling (Point markers, LineString strokes, Polygon fills)
 *
 * **Validates: Requirement 4** - Vector Layer Display
 */

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { GeoJSON } from 'ol/format';
import { Style, Fill, Stroke, Circle, RegularShape } from 'ol/style';
import type { Layer } from '../store/slices/layersSlice';
import type { Symbology, MarkerSymbol } from '../types/symbology';

/**
 * Configuration options for creating a vector layer
 */
export interface VectorLayerConfig {
  /** Layer data including features and symbology */
  layer: Layer;
  /** Whether the layer should be visible (overrides layer.visible if provided) */
  visible?: boolean;
  /** Layer z-order for stacking (overrides layer.zIndex if provided) */
  zIndex?: number;
  /** Layer opacity (overrides layer.opacity if provided) */
  opacity?: number;
  /** Enable viewport culling for large feature sets (default: true for >1000 features) */
  enableViewportCulling?: boolean;
}

export const LARGE_LAYER_FEATURE_THRESHOLD = 1000;

export function shouldEnableLargeLayerOptimizations(
  featureCount: number,
  enableViewportCulling = true
): boolean {
  return enableViewportCulling && featureCount > LARGE_LAYER_FEATURE_THRESHOLD;
}

export function requiresLargeLayerOptimizationRecreation(
  currentFeatureCount: number,
  nextFeatureCount: number,
  enableViewportCulling = true
): boolean {
  return (
    shouldEnableLargeLayerOptimizations(currentFeatureCount, enableViewportCulling) !==
    shouldEnableLargeLayerOptimizations(nextFeatureCount, enableViewportCulling)
  );
}

/**
 * Error thrown when GeoJSON validation fails
 */
export class GeoJSONValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeoJSONValidationError';
  }
}

/**
 * Error thrown when symbology configuration is invalid
 */
export class SymbologyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SymbologyError';
  }
}

/** Geometry types OpenLayers + our symbology path can render. */
const RENDERABLE_GEOMETRY_TYPES = new Set([
  'Point',
  'LineString',
  'Polygon',
  'MultiPoint',
  'MultiLineString',
  'MultiPolygon',
]);

/**
 * Explode GeometryCollection (and nested collections) into individual geometries.
 * KML/import paths often produce GeometryCollection; OL layer path does not style them.
 */
function explodeGeometry(geometry: GeoJSON.Geometry | null | undefined): GeoJSON.Geometry[] {
  if (!geometry || typeof geometry !== 'object' || !geometry.type) return [];

  if (geometry.type === 'GeometryCollection') {
    const nested = (geometry as GeoJSON.GeometryCollection).geometries || [];
    const out: GeoJSON.Geometry[] = [];
    for (const g of nested) {
      out.push(...explodeGeometry(g));
    }
    return out;
  }

  if (RENDERABLE_GEOMETRY_TYPES.has(geometry.type)) {
    // Multi* and simple types with coordinates
    if ('coordinates' in geometry && Array.isArray((geometry as any).coordinates)) {
      return [geometry];
    }
  }

  return [];
}

/**
 * Normalize layer features for map rendering:
 * - Explode GeometryCollection into one feature per child geometry
 * - Drop null / empty / unsupported geometries (no throw)
 */
export function normalizeFeaturesForMap(features: any[] | undefined | null): any[] {
  if (!Array.isArray(features)) return [];

  const normalized: any[] = [];

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];
    if (!feature || typeof feature !== 'object') continue;

    const geoms = explodeGeometry(feature.geometry as GeoJSON.Geometry);
    if (geoms.length === 0) continue;

    const baseProps =
      feature.properties &&
      typeof feature.properties === 'object' &&
      !Array.isArray(feature.properties)
        ? feature.properties
        : {};

    if (geoms.length === 1) {
      normalized.push({
        ...feature,
        id: feature.id ?? `f-${i}`,
        geometry: geoms[0],
        properties: baseProps,
      });
    } else {
      geoms.forEach((geom, gi) => {
        normalized.push({
          ...feature,
          id: `${feature.id ?? `f-${i}`}-${gi}`,
          geometry: geom,
          properties: {
            ...baseProps,
            _fromGeometryCollection: true,
            _partIndex: gi,
          },
        });
      });
    }
  }

  return normalized;
}

/**
 * Validates a GeoJSON FeatureCollection structure (after normalization preferred).
 * Skips bad features instead of throwing when `soft` is true.
 *
 * @param features - Array of features to validate
 * @throws {GeoJSONValidationError} If structure is fatally invalid (not an array)
 */
function validateGeoJSON(features: any[], soft = true): any[] {
  if (!Array.isArray(features)) {
    throw new GeoJSONValidationError('Features must be an array');
  }

  const valid: any[] = [];

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];

    if (!feature || typeof feature !== 'object') {
      if (soft) continue;
      throw new GeoJSONValidationError(`Feature at index ${i} is not an object`);
    }

    if (!feature.geometry || typeof feature.geometry !== 'object') {
      if (soft) continue;
      throw new GeoJSONValidationError(`Feature at index ${i} is missing valid geometry`);
    }

    if (!feature.geometry.type || typeof feature.geometry.type !== 'string') {
      if (soft) continue;
      throw new GeoJSONValidationError(`Feature at index ${i} has invalid geometry type`);
    }

    // GeometryCollection should already be exploded; if not, soft-skip
    if (feature.geometry.type === 'GeometryCollection') {
      if (soft) continue;
      throw new GeoJSONValidationError(
        `Feature at index ${i} has unsupported geometry type: GeometryCollection`
      );
    }

    if (!RENDERABLE_GEOMETRY_TYPES.has(feature.geometry.type)) {
      if (soft) continue;
      throw new GeoJSONValidationError(
        `Feature at index ${i} has unsupported geometry type: ${feature.geometry.type}`
      );
    }

    if (!Array.isArray(feature.geometry.coordinates)) {
      if (soft) continue;
      throw new GeoJSONValidationError(`Feature at index ${i} has invalid coordinates`);
    }

    if (feature.properties !== undefined && feature.properties !== null) {
      if (typeof feature.properties !== 'object' || Array.isArray(feature.properties)) {
        if (soft) {
          feature.properties = {};
        } else {
          throw new GeoJSONValidationError(
            `Feature at index ${i} has invalid properties (must be an object)`
          );
        }
      }
    }

    valid.push(feature);
  }

  return valid;
}

/**
 * Validates and normalizes symbology configuration safely
 *
 * @param symbology - Symbology configuration to validate and sanitize
 */
function validateSymbology(symbology: Partial<Symbology>): void {
  // Normalize color fields gracefully rather than throwing uncaught errors
  const colorFields: Array<keyof Symbology> = ['fillColor', 'strokeColor'];
  for (const field of colorFields) {
    if (symbology[field] !== undefined) {
      const color = symbology[field];
      if (typeof color !== 'string') {
        symbology[field] = field === 'fillColor' ? '#3388ff' : '#ffffff';
      } else {
        const trimmed = color.trim();
        // Allow 3, 6, or 8 digit hex, or rgb/rgba strings
        const isHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed);
        const isRgb = /^rgba?\(/.test(trimmed);
        if (!isHex && !isRgb) {
          // Normalize invalid color string to safe fallback instead of throwing error
          symbology[field] = field === 'fillColor' ? '#3388ff' : '#ffffff';
        } else if (isHex && trimmed.length === 9) {
          // 8-digit hex (#RRGGBBAA): trim to 6-digit hex (#RRGGBB)
          symbology[field] = trimmed.substring(0, 7);
        }
      }
    }
  }

  // Validate numeric fields safely
  if (symbology.strokeWidth !== undefined) {
    const width = symbology.strokeWidth;
    if (typeof width !== 'number' || isNaN(width) || width < 0) {
      symbology.strokeWidth = 1;
    }
  }

  if (symbology.opacity !== undefined) {
    const opacity = symbology.opacity;
    if (typeof opacity !== 'number' || isNaN(opacity) || opacity < 0 || opacity > 1) {
      symbology.opacity = 0.8;
    }
  }

  if (symbology.markerSize !== undefined) {
    const size = symbology.markerSize;
    if (typeof size !== 'number' || isNaN(size) || size <= 0) {
      symbology.markerSize = 8;
    }
  }
}

/**
 * Converts a hex or color string to RGBA format with opacity
 *
 * @param hexColor - Hex or color string (e.g., '#FF0000', '#F00', 'rgba(...)')
 * @param opacity - Opacity value between 0 and 1
 * @returns RGBA color string (e.g., 'rgba(255, 0, 0, 0.5)')
 */
function hexToRgba(hexColor: string, opacity: number): string {
  if (!hexColor || typeof hexColor !== 'string') {
    return `rgba(51, 136, 255, ${opacity})`;
  }

  const color = hexColor.trim();

  // If already rgba(...) return as is
  if (color.startsWith('rgba(')) {
    return color;
  }

  // If rgb(...) convert to rgba(...)
  if (color.startsWith('rgb(')) {
    const match = color.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      const parts = match[1].split(',').map((p) => p.trim());
      if (parts.length >= 3) {
        return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${opacity})`;
      }
    }
  }

  // Remove # if present
  let hex = color.replace(/^#/, '');

  // Handle 8-digit hex (#RRGGBBAA)
  if (hex.length === 8) {
    hex = hex.substring(0, 6);
  } else if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .substring(0, 3)
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // If invalid hex length or non-hex chars, fallback to safe blue
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return `rgba(51, 136, 255, ${opacity})`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return `rgba(51, 136, 255, ${opacity})`;
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Creates an OpenLayers image style for a point marker symbol.
 *
 * Supports the following marker symbols:
 *   - 'circle'   — filled circle (OpenLayers Circle style)
 *   - 'square'   — 4-point regular polygon (RegularShape, angle=Math.PI/4)
 *   - 'triangle' — 3-point regular polygon (RegularShape, angle=0)
 *   - 'star'     — 5-point star (RegularShape with radius2)
 *   - 'cross'    — 4-point cross (RegularShape, angle=0, no fill)
 *   - 'x'        — 4-point cross rotated 45° (RegularShape, angle=Math.PI/4, no fill)
 *
 * @param markerSymbol - The marker symbol identifier
 * @param radius - Marker radius in pixels
 * @param fillColor - RGBA fill color string
 * @param strokeColor - RGBA stroke color string
 * @param strokeWidth - Stroke width in pixels
 * @returns OpenLayers image style (Circle or RegularShape)
 *
 * **Validates: Requirement 6.5** – marker symbol, size, color, and opacity for point layers.
 */
function createMarkerImage(
  markerSymbol: MarkerSymbol,
  radius: number,
  fillColor: string,
  strokeColor: string,
  strokeWidth: number
): Circle | RegularShape {
  const fill = new Fill({ color: fillColor });
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });

  switch (markerSymbol) {
    case 'circle':
      return new Circle({ radius, fill, stroke });

    case 'square':
      // 4-sided regular polygon rotated 45° gives a square
      return new RegularShape({
        points: 4,
        radius,
        angle: Math.PI / 4,
        fill,
        stroke,
      });

    case 'triangle':
      // 3-sided regular polygon; angle=0 puts a point at the top
      return new RegularShape({
        points: 3,
        radius,
        angle: 0,
        fill,
        stroke,
      });

    case 'star':
      // 5-point star: outer radius = radius, inner radius = radius * 0.4
      return new RegularShape({
        points: 5,
        radius,
        radius2: radius * 0.4,
        angle: 0,
        fill,
        stroke,
      });

    case 'cross':
      // 4-point cross (plus sign): use RegularShape with radius2=0 and angle=0
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: 0,
        fill: new Fill({ color: strokeColor }), // cross arms use stroke color
        stroke,
      });

    case 'x':
      // 4-point cross rotated 45° (X shape)
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: Math.PI / 4,
        fill: new Fill({ color: strokeColor }), // X arms use stroke color
        stroke,
      });

    default:
      // Fallback to circle for unknown symbols
      return new Circle({ radius, fill, stroke });
  }
}

/**
 * Creates an OpenLayers Style from layer symbology configuration.
 *
 * Opacity precedence:
 *   1. `symbology.opacity` — set by the Symbology Editor (per-layer visual opacity)
 *   2. `layer.opacity`     — set by the opacity slider in the Layer List Panel
 *   3. Default of 1 (fully opaque)
 *
 * The resolved opacity is embedded into the fill and stroke RGBA colors so that
 * polygon fill, stroke, and point marker all respect the symbology opacity value.
 *
 * @param layer - Layer configuration with symbology
 * @param layerOpacityOverride - Optional override for the OL layer-level opacity
 * @returns OpenLayers Style instance
 *
 * **Validates: Requirement 6.2, 6.3** – Symbology updates layer display; supports
 * fill color, stroke color, stroke width, and opacity for polygon layers.
 */
function createStyleFromSymbology(layer: Layer, layerOpacityOverride?: number): Style {
  const symbology = layer.symbology || ({} as Partial<Symbology>);

  // Validate symbology if present
  if (Object.keys(symbology).length > 0) {
    validateSymbology(symbology);
  }

  // Default style values based on geometry type
  const geometryType = layer.geometryType;
  const fillColor = symbology.fillColor || '#3388ff';
  const strokeColor =
    symbology.strokeColor || (geometryType === 'LineString' ? '#3388ff' : '#ffffff');
  const strokeWidth = symbology.strokeWidth ?? (geometryType === 'LineString' ? 3 : 2);
  const markerSize = symbology.markerSize || 8;

  // Resolve opacity: symbology.opacity takes precedence over layer.opacity.
  // This ensures that changes made in the Symbology Editor are immediately
  // reflected in the rendered fill and stroke colors.
  const finalOpacity =
    symbology.opacity !== undefined
      ? symbology.opacity
      : layerOpacityOverride !== undefined
        ? layerOpacityOverride
        : layer.opacity !== undefined
          ? layer.opacity
          : 1;

  // Convert hex colors to rgba with opacity
  const fillRgba = hexToRgba(fillColor, finalOpacity);
  const strokeRgba = hexToRgba(strokeColor, finalOpacity);

  // Create base style configuration
  const styleConfig: any = {
    fill: new Fill({
      color: fillRgba,
    }),
    stroke: new Stroke({
      color: strokeRgba,
      width: strokeWidth,
    }),
  };

  // Add image style only for Point geometry types
  // LineString and Polygon features don't need marker symbols
  if (geometryType === 'Point') {
    const markerSymbol = (symbology.markerSymbol ?? 'circle') as MarkerSymbol;
    styleConfig.image = createMarkerImage(
      markerSymbol,
      markerSize,
      fillRgba,
      strokeRgba,
      strokeWidth
    );
  }

  return new Style(styleConfig);
}

/**
 * Converts GeoJSON features to an OpenLayers VectorLayer
 *
 * This function takes a Layer configuration with GeoJSON features and creates
 * an OpenLayers VectorLayer with proper styling, visibility, and z-order.
 *
 * For layers with more than 1000 features, viewport culling is automatically enabled
 * to optimize rendering performance. This ensures only features visible in the current
 * viewport are rendered, significantly improving map interaction performance.
 *
 * @param config - Configuration object containing layer data and optional overrides
 * @returns OpenLayers VectorLayer instance
 * @throws {GeoJSONValidationError} If the GeoJSON structure is invalid
 * @throws {SymbologyError} If the symbology configuration is invalid
 *
 * @example
 * ```typescript
 * const layer: Layer = {
 *   id: 'layer-1',
 *   name: 'My Layer',
 *   type: 'vector',
 *   geometryType: 'Point',
 *   visible: true,
 *   opacity: 0.8,
 *   zIndex: 1,
 *   features: [
 *     {
 *       id: 'feature-1',
 *       layerId: 'layer-1',
 *       geometry: { type: 'Point', coordinates: [0, 0] },
 *       properties: { name: 'Feature 1' }
 *     }
 *   ],
 *   symbology: {
 *     fillColor: '#FF0000',
 *     strokeColor: '#000000',
 *     strokeWidth: 2,
 *     markerSize: 10
 *   },
 *   crs: 'EPSG:4326'
 * };
 *
 * const vectorLayer = geojsonToVectorLayer({ layer });
 * map.addLayer(vectorLayer);
 * ```
 */
export function geojsonToVectorLayer(config: VectorLayerConfig): any {
  const { layer, visible, zIndex, opacity, enableViewportCulling } = config;

  // Explode GeometryCollection + drop unrenderable geometries (never crash the map)
  const features = validateGeoJSON(normalizeFeaturesForMap(layer.features || []), true);

  // Create GeoJSON FeatureCollection
  const geojsonFeatures = features.map((feature) => ({
    type: 'Feature' as const,
    id: feature.id,
    geometry: feature.geometry,
    properties: feature.properties || {},
  }));

  const geojsonData: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: geojsonFeatures,
  };

  // Determine if large-layer rendering optimizations should be enabled
  const shouldEnableViewportCulling = shouldEnableLargeLayerOptimizations(
    features.length,
    enableViewportCulling !== false
  );

  // Create vector source with GeoJSON data
  const vectorSource = new VectorSource({
    features: new GeoJSON().readFeatures(geojsonData, {
      featureProjection: 'EPSG:3857', // Web Mercator for display
      dataProjection: layer.crs || 'EPSG:4326', // Layer CRS (default WGS84)
    }),
    // Enable spatial indexing for efficient viewport queries
    // This uses an RTree internally for fast spatial lookups
    useSpatialIndex: shouldEnableViewportCulling,
  });

  // Create style from symbology
  const style = createStyleFromSymbology(layer, opacity);

  // Determine final configuration values
  const finalVisible = visible !== undefined ? visible : layer.visible;
  const finalZIndex = zIndex !== undefined ? zIndex : layer.zIndex;
  const finalOpacity =
    opacity !== undefined ? opacity : layer.opacity !== undefined ? layer.opacity : 1;

  // Create vector layer with style
  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: style,
    zIndex: finalZIndex + 1, // Add 1 to ensure it's above basemap (zIndex 0)
    visible: finalVisible,
    opacity: finalOpacity,
    // Enable viewport culling for large feature sets
    // This tells OpenLayers to only render features within the current view extent
    renderBuffer: shouldEnableViewportCulling ? 100 : 250, // Smaller buffer for large datasets
    updateWhileAnimating: !shouldEnableViewportCulling, // Disable updates during animation for large datasets
    updateWhileInteracting: !shouldEnableViewportCulling, // Disable updates during interaction for large datasets
  });

  // Store layer metadata for identification
  (vectorLayer as any).layerId = layer.id;
  (vectorLayer as any).layerName = layer.name;
  (vectorLayer as any).geometryType = layer.geometryType;
  (vectorLayer as any).featureCount = features.length;
  (vectorLayer as any).viewportCullingEnabled = shouldEnableViewportCulling;

  return vectorLayer;
}

/**
 * Updates an existing OpenLayers VectorLayer with new data
 *
 * This function updates an existing vector layer's features, style, visibility,
 * opacity, and z-index without recreating the layer. For layers with >1000 features,
 * viewport culling optimizations are automatically applied.
 *
 * @param vectorLayer - Existing OpenLayers VectorLayer to update
 * @param config - New configuration to apply
 * @throws {GeoJSONValidationError} If the GeoJSON structure is invalid
 * @throws {SymbologyError} If the symbology configuration is invalid
 *
 * @example
 * ```typescript
 * const vectorLayer = geojsonToVectorLayer({ layer: originalLayer });
 *
 * // Later, update the layer with new data
 * const updatedLayer = { ...originalLayer, visible: false };
 * updateVectorLayer(vectorLayer, { layer: updatedLayer });
 * ```
 */
export function updateVectorLayer(vectorLayer: any, config: VectorLayerConfig): void {
  const { layer, visible, zIndex, opacity, enableViewportCulling } = config;

  // Normalize + soft-validate (GeometryCollection etc. must not throw)
  const features = validateGeoJSON(normalizeFeaturesForMap(layer.features || []), true);

  // Update visibility
  const finalVisible = visible !== undefined ? visible : layer.visible;
  vectorLayer.setVisible(finalVisible);

  // Update opacity
  const finalOpacity =
    opacity !== undefined ? opacity : layer.opacity !== undefined ? layer.opacity : 1;
  vectorLayer.setOpacity(finalOpacity);

  // Update z-index
  const finalZIndex = zIndex !== undefined ? zIndex : layer.zIndex;
  vectorLayer.setZIndex(finalZIndex + 1);

  // Update style
  const style = createStyleFromSymbology(layer, finalOpacity);
  vectorLayer.setStyle(style);

  // Determine if large-layer rendering optimizations should be enabled
  const shouldEnableViewportCulling = shouldEnableLargeLayerOptimizations(
    features.length,
    enableViewportCulling !== false
  );

  // renderBuffer, updateWhileAnimating, updateWhileInteracting, and useSpatialIndex
  // are constructor-only OpenLayers settings. Callers must recreate the layer when
  // requiresLargeLayerOptimizationRecreation reports a threshold transition.

  // Update features
  const source = vectorLayer.getSource();
  if (source) {
    const geojsonFeatures = features.map((feature) => ({
      type: 'Feature' as const,
      id: feature.id,
      geometry: feature.geometry,
      properties: feature.properties || {},
    }));

    const geojsonData: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: geojsonFeatures,
    };

    // Clear existing features and add new ones
    source.clear();
    const newFeatures = new GeoJSON().readFeatures(geojsonData, {
      featureProjection: 'EPSG:3857',
      dataProjection: layer.crs || 'EPSG:4326',
    });
    source.addFeatures(newFeatures);
  }

  // Update metadata
  (vectorLayer as any).layerId = layer.id;
  (vectorLayer as any).layerName = layer.name;
  (vectorLayer as any).geometryType = layer.geometryType;
  (vectorLayer as any).featureCount = features.length;
  (vectorLayer as any).viewportCullingEnabled = shouldEnableViewportCulling;
}

/**
 * Converts a `Symbology` object and geometry type into an OpenLayers `Style`.
 *
 * This is the public, standalone style-factory for task 8.7. It is geometry-type-aware:
 *   - **Polygon**: fill color, stroke color, stroke width, opacity
 *   - **LineString**: stroke color, stroke width, opacity (no fill image)
 *   - **Point**: marker symbol, marker size, fill color, stroke color, opacity
 *
 * Opacity is read from `symbology.opacity` (defaults to 1 if not set).
 *
 * @param symbology - The Symbology configuration to convert
 * @param geometryType - The geometry type of the layer ('Point' | 'LineString' | 'Polygon')
 * @returns An OpenLayers `Style` instance ready to be passed to `vectorLayer.setStyle()`
 *
 * @example
 * ```typescript
 * const style = createOLStyleFromSymbology(
 *   { type: 'simple', fillColor: '#ff0000', strokeColor: '#000000', strokeWidth: 2, opacity: 0.8 },
 *   'Polygon'
 * );
 * vectorLayer.setStyle(style);
 * ```
 *
 * **Validates: Requirements 6.2, 6.3, 6.4, 6.5** – Vector_Layer_Manager SHALL
 * update the layer display with new symbology; supports fill color, stroke color,
 * stroke width, and opacity for polygon layers; stroke color, stroke width, and
 * opacity for line layers; marker symbol, size, color, and opacity for point layers.
 */
export function createOLStyleFromSymbology(
  symbology: Symbology,
  geometryType: 'Point' | 'LineString' | 'Polygon'
): Style {
  // Validate the incoming symbology
  validateSymbology(symbology);

  // Delegate to the internal helper by constructing a minimal Layer-like object
  const pseudoLayer: Layer = {
    id: '',
    name: '',
    type: 'vector',
    geometryType,
    visible: true,
    opacity: 1,
    zIndex: 0,
    symbology,
  };

  return createStyleFromSymbology(pseudoLayer);
}

/**
 * Applies a Symbology configuration to an existing OpenLayers VectorLayer.
 *
 * This is the primary entry point for task 8.7 — it converts a `Symbology`
 * object into an OpenLayers `Style` and immediately applies it to the given
 * vector layer so that the map display updates in real-time.
 *
 * The function is geometry-type-aware:
 *   - **Polygon**: fill color, stroke color, stroke width, opacity
 *   - **LineString**: stroke color, stroke width, opacity
 *   - **Point**: marker symbol, marker size, fill color, stroke color, opacity
 *
 * Opacity precedence (highest → lowest):
 *   1. `symbology.opacity`
 *   2. `layerOpacity` parameter
 *   3. Default of 1 (fully opaque)
 *
 * @param vectorLayer - The OpenLayers VectorLayer to update
 * @param layer - The Redux layer descriptor (provides geometry type and current opacity)
 * @param symbology - The new Symbology to apply
 *
 * @example
 * ```typescript
 * // In a React component or Redux middleware:
 * const olLayer = vectorLayersRef.current.get(layerId);
 * if (olLayer) {
 *   applySymbologyToLayer(olLayer, reduxLayer, newSymbology);
 * }
 * ```
 *
 * **Validates: Requirements 6.2, 6.3, 6.4, 6.5** – Vector_Layer_Manager SHALL
 * update the layer display with new symbology; supports fill color, stroke color,
 * stroke width, and opacity for polygon layers; stroke color, stroke width, and
 * opacity for line layers; marker symbol, size, color, and opacity for point layers.
 */
export function applySymbologyToLayer(
  vectorLayer: any, // @ts-expect-error OL typing for robustness
  layer: Layer,
  symbology: Symbology
): void {
  // Validate the incoming symbology
  validateSymbology(symbology);

  // Build a temporary layer descriptor with the new symbology so we can
  // reuse the existing createStyleFromSymbology helper.
  const layerWithNewSymbology: Layer = {
    ...layer,
    symbology,
  };

  const style = createStyleFromSymbology(layerWithNewSymbology);
  vectorLayer.setStyle(style);
}

/**
 * Batch converts multiple layers to OpenLayers VectorLayers
 *
 * @param layers - Array of layer configurations
 * @returns Array of OpenLayers VectorLayer instances
 * @throws {GeoJSONValidationError} If any GeoJSON structure is invalid
 * @throws {SymbologyError} If any symbology configuration is invalid
 *
 * @example
 * ```typescript
 * const layers: Layer[] = [layer1, layer2, layer3];
 * const vectorLayers = batchConvertLayers(layers);
 * vectorLayers.forEach(layer => map.addLayer(layer));
 * ```
 */
export function batchConvertLayers(layers: Layer[]): any[] {
  return layers.map((layer) => geojsonToVectorLayer({ layer }));
}
